// ConditionalAuthProvider.tsx - Conditional Authentication Wrapper

import React, { ReactNode, createContext, useContext, useState, useEffect, useSyncExternalStore } from 'react';
import { ReactKeycloakProvider } from '@react-keycloak/web';
import { Provider } from 'react-redux';
import keycloak, { terminateKeycloakSession } from '../keycloak';
import {
  getKeycloakRefreshIntervalMs,
  getKeycloakRefreshMinValiditySeconds,
  isKeycloakEnabled,
} from '../config/authConfig';
import authService, { AuthUser } from '../services/authService';
import store from '../redux/store/store';
import '../services/sessionReset';
import { getSessionEpoch, subscribeSessionEpoch, syncSessionIdentity } from '../services/sessionIdentity';

interface ConditionalAuthProviderProps {
  children: ReactNode;
  LoadingComponent?: ReactNode;
}

// Authentication context for non-Keycloak mode
interface AuthContextType {
  user: AuthUser | null;
  authenticated: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  initialized: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

// Remounts the app subtree whenever the signed-in identity changes or ends, so no
// component state (search text, selections, open details, ...) outlives its user.
const SessionBoundary: React.FC<{ children: ReactNode }> = ({ children }) => {
  const sessionEpoch = useSyncExternalStore(subscribeSessionEpoch, getSessionEpoch);
  return <React.Fragment key={sessionEpoch}>{children}</React.Fragment>;
};

// Account key of the current Keycloak session ('' when signed out).
const readKeycloakIdentity = (): string =>
  keycloak?.authenticated ? String(keycloak.tokenParsed?.sub ?? keycloak.tokenParsed?.preferred_username ?? '') : '';

// Real authentication provider for non-Keycloak mode
const RealAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUserState] = useState<AuthUser | null>(null);
  const [initialized, setInitialized] = useState(false);
  const setUser = (nextUser: AuthUser | null) => {
    syncSessionIdentity(nextUser ? String(nextUser.id) : '');
    setUserState(nextUser);
  };

  useEffect(() => {
    // Initialize authentication state
    const initializeAuth = async () => {
      try {
        if (authService.isAuthenticated()) {
          const currentUser = authService.getCurrentUser();
          if (currentUser) {
            setUser(currentUser);
            console.log('🔐 User authenticated:', currentUser);
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        authService.logout();
      } finally {
        setInitialized(true);
      }
    };

    initializeAuth();
    // Listen for forced logout events (token expiry or manual)
    const onLogout = () => {
      setUser(null);
    };
    window.addEventListener('dms-auth-logout', onLogout as EventListener);
    return () => window.removeEventListener('dms-auth-logout', onLogout as EventListener);
  }, []);

  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      const user = await authService.login({ username, password });
      setUser(user);
      return true;
    } catch (error) {
      console.error('Login failed:', error);
      return false;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await authService.logout();
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const contextValue: AuthContextType = {
    user,
    authenticated: !!user,
    login,
    logout,
    initialized
  };

  return (
    <AuthContext.Provider value={contextValue}>
      <SessionBoundary>{children}</SessionBoundary>
    </AuthContext.Provider>
  );
};

// Hook to use authentication context (only available in non-Keycloak mode)
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Unified hook that works in both Keycloak and non-Keycloak modes
export const useKeycloak = () => {
  const authContext = useContext(AuthContext);
  
  if (authContext) {
    // Non-Keycloak mode: return mock Keycloak-like interface
    return {
      keycloak: {
        authenticated: authContext.authenticated,
        token: authService.getValidToken(false),
        tokenParsed: authContext.user ? {
          sub: authContext.user.id.toString(),
          preferred_username: authContext.user.username,
          email: authContext.user.email,
          capabilities: authContext.user.capabilities,
          realm_access: {
            roles: authContext.user.roles
          }
        } : null,
        login: () => Promise.resolve(true),
        logout: () => authContext.logout(),
        hasRealmRole: (role: string) => authContext.user?.roles.includes(role) || false,
        hasResourceRole: (role: string) => authContext.user?.roles.includes(role) || false,
        loadUserProfile: () => Promise.resolve(authContext.user),
        updateToken: () => Promise.resolve(true),
        init: () => Promise.resolve(true),
        createLoginUrl: () => '/login',
        createLogoutUrl: () => '/logout',
        isTokenExpired: () => false,
        refreshToken: 'mock-refresh-token',
        refreshTokenParsed: {},
        idToken: 'mock-id-token',
        idTokenParsed: {},
        realm: 'mock-realm',
        clientId: 'mock-client',
        clientConfig: {},
        loginRequired: false,
        onReady: () => {},
        onAuthSuccess: () => {},
        onAuthError: () => {},
        onAuthRefreshSuccess: () => {},
        onAuthRefreshError: () => {},
        onAuthLogout: () => {},
        onTokenExpired: () => {},
        onActionUpdate: () => {},
        onActionRequired: () => {}
      },
      initialized: authContext.initialized
    };
  }
  
  // Keycloak mode: use the real Keycloak hook
  try {
    const { useKeycloak: realUseKeycloak } = require('@react-keycloak/web');
    return realUseKeycloak();
  } catch (error) {
    console.warn('Keycloak not available, using fallback');
    return { keycloak: null, initialized: false };
  }
};

// Safe hook that works in both modes
export const useAuthSafe = () => {
  const authContext = useContext(AuthContext);
  
  if (authContext) {
    // Non-Keycloak mode
    return {
      authenticated: authContext.authenticated,
      initialized: authContext.initialized,
      user: authContext.user,
      login: authContext.login,
      logout: authContext.logout
    };
  }
  
  // Keycloak mode - return safe defaults
  return {
    authenticated: false,
    initialized: false,
    user: null,
    login: async () => false,
    logout: async () => {}
  };
};

const ConditionalAuthProvider: React.FC<ConditionalAuthProviderProps> = ({
  children,
  LoadingComponent
}) => {
  const refreshIntervalMs = getKeycloakRefreshIntervalMs();
  const minValiditySeconds = getKeycloakRefreshMinValiditySeconds();

  useEffect(() => {
    if (!isKeycloakEnabled() || !keycloak || typeof window === 'undefined') {
      return;
    }
    const keycloakClient = keycloak;

    let cancelled = false;
    const refreshToken = async () => {
      if (!keycloakClient.authenticated || typeof keycloakClient.updateToken !== 'function') {
        return;
      }

      try {
        await keycloakClient.updateToken(minValiditySeconds);
      } catch (error) {
        if (!cancelled) {
          console.error('Keycloak proactive token refresh failed:', error);
          await terminateKeycloakSession('refresh-failure');
        }
      }
    };

    void refreshToken();
    const refreshHandle = window.setInterval(() => {
      void refreshToken();
    }, refreshIntervalMs);

    return () => {
      cancelled = true;
      window.clearInterval(refreshHandle);
    };
  }, [minValiditySeconds, refreshIntervalMs]);

  if (isKeycloakEnabled()) {
    // Use Keycloak authentication
    console.log('🔐 Using Keycloak authentication');
    return (
      <Provider store={store}>
        <ReactKeycloakProvider
          authClient={keycloak!}
          initOptions={{
            onLoad: 'login-required',
            checkLoginIframe: false
          }}
          LoadingComponent={LoadingComponent as React.ReactElement}
          onEvent={async (event, error) => {
            console.log('Keycloak event:', event, error);
            // keycloak-js has already swapped its token when these fire (silent
            // re-auth / refresh can bring another account into this JS instance).
            syncSessionIdentity(readKeycloakIdentity());
            if (event === 'onTokenExpired' || event === 'onAuthRefreshError') {
              try {
                if (!keycloak || typeof keycloak.updateToken !== 'function') {
                  throw new Error('Keycloak updateToken is unavailable');
                }
                await keycloak.updateToken(minValiditySeconds);
              } catch (refreshError) {
                console.error('Keycloak refresh failed during event handling:', refreshError);
                await terminateKeycloakSession('event-refresh-failure');
              }
            }
            if (event === 'onAuthLogout' && typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('dms-auth-logout'));
            }
          }}
          onTokens={(tokens) => {
            console.log('Keycloak tokens updated', !!tokens?.token);
          }}
        >
          <SessionBoundary>{children}</SessionBoundary>
        </ReactKeycloakProvider>
      </Provider>
    );
  } else {
    // Use real backend authentication (no Keycloak)
    console.log('🔓 Keycloak disabled - using backend authentication');
    return (
      <Provider store={store}>
        <RealAuthProvider>
          {children}
        </RealAuthProvider>
      </Provider>
    );
  }
};

export default ConditionalAuthProvider;
